/**
 * Source Adapter
 * Transforms source-related data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type {
  Source,
  SourceStats,
  CreateSourceDto,
  UpdateSourceDto,
  SourceResponseDto,
  SourceStatsResponseDto,
  SourcesListResponseDto
} from '@/types';

export class SourceAdapter extends BaseAdapter {
  /**
   * Adapts source from API response
   */
  static fromApi(dto: SourceResponseDto): Source {
    return {
      id: dto.id,
      name: dto.name,
      type: dto.type.toUpperCase() as Source['type'],
      value: dto.value,
      status: dto.status,
      accountId: dto.accountId,
      createdBy: dto.createdBy,
      metadata: dto.metadata || {},
      categrizationId: dto.categrizationId ?? null,
      categorizeMiss: dto.categorizeMiss,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,
      queryUsageCount: dto.queryUsageCount ?? 0
    };
  }

  /**
   * Adapts sources array from API response
   */
  static fromApiList(dtos: SourceResponseDto[]): Source[] {
    return dtos.map((dto) => this.fromApi(dto));
  }

  /**
   * Adapts a paginated sources list response, keeping page/totalPages/total instead of
   * discarding them — the caller needs these to render real pagination controls.
   */
  static fromListApi(dto: SourcesListResponseDto): {
    sources: Source[];
    pagination: { page: number; totalPages: number; total: number };
  } {
    return {
      sources: (dto.sources ?? []).map((s) => this.fromApi(s)),
      pagination: {
        page: dto.page ?? 1,
        totalPages: dto.totalPages ?? 1,
        total: dto.count ?? 0
      }
    };
  }

  /**
   * Adapts create source request
   */
  /**
   * The list query as the API expects it.
   *
   * `type` is the third place this enum has to cross the case boundary, and the only one
   * that was missed. The UI works in 'LINK'; Mongo stores 'link' (SourceType in
   * kim-ai-backend's models/mongoModels/sources.ts). `fromApi` uppercases on the way in
   * and `toCreateRequest` lowercases on the way out — a filter has to do the same, or the
   * server compares 'LINK' against 'link' and matches nothing.
   *
   * It failed silently: a case-sensitive equality on a valid-looking value returns an
   * empty page rather than an error, so every chip except "All" showed an empty table that
   * read as "no sources of this kind". It worked until filtering moved from the client to
   * the server (00ba8e38), because the client compared against the ALREADY-uppercased
   * `source.type` and the two agreed.
   */
  static toListQuery<T extends { type?: string }>(params: T): T {
    return params.type ? { ...params, type: params.type.toLowerCase() } : params;
  }


  static toCreateRequest(data: CreateSourceDto): CreateSourceDto {
    return this.sanitizePayload({
      name: data.name.trim(),
      type: data.type.toLowerCase() as CreateSourceDto['type'],
      value: data.value.trim(),
      metadata: data.metadata
    });
  }

  /**
   * Adapts update source request
   */
  static toUpdateRequest(data: UpdateSourceDto): UpdateSourceDto {
    const payload: UpdateSourceDto = {};

    if (data.name !== undefined) payload.name = data.name.trim();
    if (data.value !== undefined) payload.value = data.value.trim();
    if (data.status !== undefined) payload.status = data.status;
    if (data.metadata !== undefined) payload.metadata = data.metadata;

    return this.sanitizePayload(payload);
  }

  /**
   * Adapts stats from API response
   */
  static fromStatsApi(dto: SourceStatsResponseDto): SourceStats {
    return {
      total: dto.total || 0,
      successCount: dto.successCount,
      failedCount: dto.failedCount,
      inProgressCount: dto.inProgressCount,
      byType: dto.byType
    };
  }
}
